import AttachFileIcon from "@mui/icons-material/AttachFile";
import FirestoreIcon from "@mui/icons-material/LocalFireDepartment";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { CSSProperties, forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  CreateBase,
  EditBase,
  RecordContextProvider,
  SaveButton,
  SimpleForm,
  TextInput,
  useGetOne,
  useInput,
  useRecordContext,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { useQueryClient } from "react-query";
import { DateField } from "../fields/DateField";
import { useThreadId } from "../hooks/useThreadId.js";
import { autovioColors } from "../misc/backofficeTheme";
import { Spinner } from "../misc/Spinner";
import { type Post, type Thread } from "../model/Thread.js";
import { User, usersProvider } from "../providers/usersProvider";
import { gcs } from "../utils/storage";
import { Row } from "../misc/Row";
import { Column } from "../misc/Column";
import { useThemeColor } from "../hooks/useThemeColor";
import { DeleteIcon } from "../icons/DeleteIcon";
import { currentUser, grants } from "../backoffice.access_control";
import { openInFirestoreConsole } from "../firebase";
import { useMenu } from "../hooks/useMenu";
import { useDropzone } from "react-dropzone";
import { HiddenInput } from "../inputs/HiddenInput";
import { postsProvider } from "../providers/postsProvider";
import { openDownloadWindow } from "../utils/openDownloadWindow";
import { truncateFileName } from "../utils/truncateFileName";

export const PostsList = forwardRef(({ title, style }: { title?: ReactNode; style?: CSSProperties }, ref) => {
  return (
    <div style={{ position: "relative", ...style }}>
      <_PostsList title={title} ref={ref} />
    </div>
  );
});

const _PostsList = forwardRef(({ title }: { title?: ReactNode }, ref) => {
  const threadId = useThreadId();
  const { data: thread, isLoading, refetch } = useGetOne<Thread>("threads", { id: threadId }, { enabled: !!threadId });
  const data = thread?.sortedPosts;
  const [state, setState] = useState<"show button" | "show form">("show button");
  useImperativeHandle(ref, function () {
    return {
      showForm: () => setState("show form"),
    };
  });

  if (isLoading || !thread) {
    return (
      <>
        {title}
        <div style={{ flex: 1 }}>
          <Spinner style={{ margin: "20px" }} />
        </div>
      </>
    );
  }

  const titleAndButton = state === "show button" && (
    <div style={{ display: "flex", justifyContent: "space-between" }}>{title ?? <div />}</div>
  );
  const titleAndForm = state === "show form" && (
    <>
      {title}
      <_AddPostForm threadId={thread.id} close={() => setState("show button")} />
    </>
  );

  if (!data || data.length === 0) {
    return (
      titleAndForm || (
        <>
          {titleAndButton}
          <Typography
            variant="body2"
            sx={{
              height: "20px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              color: "text.secondary",
            }}
          >
            Keine Notizen vorhanden.
          </Typography>
        </>
      )
    );
  }
  return (
    <>
      {titleAndButton || titleAndForm}
      <List sx={{ paddingTop: 0 }}>
        {data.map((post) => (
          <RecordContextProvider key={post.id} value={post}>
            <_PostListItem refetch={refetch} />
          </RecordContextProvider>
        ))}
      </List>
    </>
  );
});

function useCreatedBy(post: Post): User | undefined {
  const id = post.createdById;
  const { data: author } = useGetOne<User>("users", { id }, { enabled: !!id });
  return author ?? { id, name: "", avatarUrl: usersProvider.getAvatarUrl(id) };
}

function useUpdatedBy(post: Post): User | undefined {
  const id = post.updatedById;
  const { data: editor } = useGetOne<User>("users", { id }, { enabled: !!id });
  return editor ?? { id, name: "", avatarUrl: usersProvider.getAvatarUrl(id) };
}

function useDeletedBy(post: Post): User | undefined {
  const { data } = useGetOne<User>("users", { id: post.deletedById ?? "" }, { enabled: !!post.deletedById });
  return data;
}

function _PostListItem({ refetch }: { refetch: () => void }) {
  const post = useRecordContext<Post>();
  const author = useCreatedBy(post);
  const editor = useUpdatedBy(post);
  const deletor = useDeletedBy(post);
  const [showActions, setShowActions] = useState(false);
  const [mode, setMode] = useState<"show" | "edit">("show");
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  if (mode === "edit") {
    return <_EditPostForm post={post} close={() => setMode("show")} />;
  }

  const deletePost = async () => {
    try {
      await postsProvider.delete("", { id: post.id });
      refetch();
      showSuccessToast("Notiz erfolgreich gelöscht.");
    } catch (error) {
      handleError("Fehler beim Löschen der Notiz.", error);
    }
  };
  const canDelete = grants.has("Post:delete") || (author && author.id === currentUser?.uid);

  return (
    <div
      style={{ position: "relative", marginBottom: 30 }}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <ListItem disablePadding>
        <Row spacing={1} sx={{ opacity: post.deleted ? 0.7 : 1 }}>
          <Avatar src={author?.avatarUrl} sx={{ width: 20, height: 20, marginTop: 0.5 }} />
          <Column>
            <Box pr={2} mb={0.5} sx={{ lineHeight: "14px" }}>
              <DateField
                className="RaLabeled-label"
                source="createdAt"
                showTime
                sx={{
                  fontWeight: "bold",
                  lineHeight: "14px",
                  fontSize: "14px !important",
                  textDecoration: post.deleted ? "line-through" : "none",
                }}
              />
              {author?.name && (
                <Box
                  component="span"
                  className="RaLabeled-label"
                  sx={{ color: "text.secondary", fontSize: "12px", lineHeight: "14px" }}
                >{` ${author.name}`}</Box>
              )}
              {post.updatedAt && post.createdAt && post.updatedAt > post.createdAt && (
                <>
                  <Box component="span" className="RaLabeled-label" sx={{ color: "text.secondary", fontSize: 12 }}>
                    {" (geändert: "}
                  </Box>
                  <DateField
                    className="RaLabeled-label"
                    source="updatedAt"
                    showTime
                    sx={{ color: "text.secondary", fontSize: "12px !important" }}
                  />
                  <Box component="span" className="RaLabeled-label" sx={{ color: "text.secondary", fontSize: 12 }}>
                    {editor?.name ? `, ${editor.name})` : ")"}
                  </Box>
                </>
              )}
              {(showActions || open) && (
                <>
                  {!post.deleted && (
                    <>
                      <IconButton onClick={openMenu} sx={{ position: "absolute", right: 0, top: "-10px" }}>
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={closeMenu}
                        sx={(theme) => ({ zIndex: theme.zIndex.modal + 2 })}
                        transformOrigin={{ vertical: "top", horizontal: "center" }}
                      >
                        <MenuItem
                          onClick={() => {
                            setMode("edit");
                            closeMenu();
                          }}
                        >
                          Bearbeiten
                        </MenuItem>
                        {canDelete && (
                          <MenuItem
                            onClick={() => {
                              closeMenu();
                              void deletePost();
                            }}
                          >
                            Löschen
                          </MenuItem>
                        )}
                      </Menu>
                    </>
                  )}
                  {grants.has("viewFirestore") && (
                    <IconButton
                      sx={{ position: "absolute", top: "-10px", right: post.deleted ? 0 : "36px" }}
                      onClick={() => openInFirestoreConsole(`/threads/${post.threadId}`)}
                    >
                      <FirestoreIcon />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
            <_PostText>{post.body ?? ""}</_PostText>
            <Box sx={{ display: "flexWrap", mt: 0.75 }}>
              {(post.attachments ?? []).map((it) => {
                const fileName = it.name;
                return (
                  <Chip
                    key={it.id}
                    sx={{
                      marginTop: "3px",
                      marginRight: "3px",
                      height: 24,
                      fontSize: "10px",
                      fontWeight: 500,
                      color: "text.secondary",
                      backgroundColor: "action.hover",
                      maxWidth: {
                        xs: "calc(100vw - 160px)",
                        md: "calc(((100vw - 360px) / 3) - 110px)",
                      },
                      "& .MuiChip-label": {
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      },
                    }}
                    onClick={async () => {
                      const attachmentWindow = openDownloadWindow();
                      try {
                        const downloadUrl = await gcs.getDownloadUrl(it.path);
                        attachmentWindow.navigate(downloadUrl);
                      } catch (error) {
                        attachmentWindow.close();
                        handleError("Fehler beim Öffnen der angehängten Datei.", error);
                      }
                    }}
                    icon={<AttachFileIcon sx={{ fontSize: 14, color: "text.secondary" }} />}
                    label={
                      fileName.length >= 24 ? (
                        <Tooltip title={fileName}>
                          <span>{truncateFileName(fileName, 27)}</span>
                        </Tooltip>
                      ) : (
                        fileName
                      )
                    }
                  />
                );
              })}
            </Box>
            {post.deleted && post.deletedAt && deletor && (
              <Row color={autovioColors.red} fontSize="16px" gap={0.5} alignItems="center" mt={0.25}>
                <DeleteIcon fontSize="inherit" />
                <Typography variant="body2">
                  Gelöscht am {post.deletedAt.toFormat("d.M.yyyy")} von {deletor.name}
                </Typography>
              </Row>
            )}
          </Column>
        </Row>
      </ListItem>
    </div>
  );
}

function _PostText({ children: text, maxLines = 5 }: { children: string; maxLines?: number }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const primaryColor = useThemeColor("primary");

  useEffect(() => {
    const checkIfTruncated = () => {
      const textElement = textRef.current;
      if (textElement) {
        const lineHeight = parseFloat(window.getComputedStyle(textElement).lineHeight);
        const maxHeight = lineHeight * maxLines + 1;
        setIsTruncated(textElement.scrollHeight > maxHeight);
      }
    };

    checkIfTruncated();

    window.addEventListener("resize", checkIfTruncated);
    return () => window.removeEventListener("resize", checkIfTruncated);
  }, [text, maxLines]);

  return (
    <>
      <Typography
        ref={textRef}
        sx={{
          maxHeight: isExpanded ? "none" : `${maxLines * 1.2}em`,
          overflow: "hidden",
          lineHeight: "1.2em",
          textOverflow: "ellipsis",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          wordBreak: "break-word",
          fontSize: 14,
          padding: 0,
        }}
      >
        {text}
      </Typography>
      {isTruncated && !isExpanded && (
        <span style={{ color: primaryColor.main, cursor: "pointer", fontSize: 14 }} onClick={() => setIsExpanded(true)}>
          mehr anzeigen
        </span>
      )}
    </>
  );
}

function _AddPostForm({ threadId, close }: { threadId: string; close: () => void }) {
  const queryClient = useQueryClient();

  return (
    <CreateBase
      resource="posts"
      mutationOptions={{
        onSuccess: async () => {
          await queryClient.invalidateQueries(["threads", "getOne", { id: threadId }]);
          close();
        },
      }}
    >
      <_PostForm mode="create" close={close} />
    </CreateBase>
  );
}

function _EditPostForm({ post, close }: { post: Post; close: () => void }) {
  const queryClient = useQueryClient();
  return (
    <EditBase
      resource="posts"
      id={post.id}
      redirect={false}
      className="edit-post"
      mutationMode="pessimistic"
      sx={{
        marginTop: 0,
        marginBottom: "8px",
        border: "1px solid",
        borderColor: "divider",
      }}
      mutationOptions={{
        onSuccess: async () => {
          await queryClient.invalidateQueries(["threads", "getOne", { id: post.threadId }]);
          close();
        },
      }}
    >
      <_PostForm mode="edit" close={close} />
    </EditBase>
  );
}

function _PostForm({ mode, close }: { mode: "create" | "edit"; close: () => void }) {
  const ref = useRef<{ openSelectFilesDialog: () => void }>();

  const validate = (values: Record<string, any>) => {
    const errors: { [field: string]: string } = {};
    if (!values.body || values.body.trim().length === 0) {
      errors.body = "Eine Notiz darf nicht leer sein.";
    }
    return errors;
  };

  return (
    <SimpleForm
      sx={{ paddingLeft: 0, paddingRight: 0, "&.MuiCardContent-root": { pt: 0 } }}
      validate={validate}
      toolbar={
        <_PostActions
          openSelectFilesDialog={mode === "create" && (() => ref.current!.openSelectFilesDialog())}
          close={close}
        />
      }
    >
      <_PostFormFields ref={ref} />
    </SimpleForm>
  );
}

const _PostFormFields = forwardRef(function _PostFormFields(_, ref) {
  // Copied and adapted from react-admin FileInput.tsx ...
  const {
    id,
    field: { onChange, onBlur, value },
  } = useInput({ source: "files" });
  const files = value ? (Array.isArray(value) ? value : [value]) : [];

  const onDrop = (newFiles: any) => {
    onChange([...files, ...newFiles]);
    onBlur();
  };

  const {
    getRootProps,
    getInputProps,
    open: openSelectFilesDialog,
  } = useDropzone({
    multiple: true,
    noClick: true,
    onDrop,
  });

  useImperativeHandle(ref, () => ({ openSelectFilesDialog }), []);

  return (
    <div {...getRootProps()} style={{ width: "100%" }}>
      <input id={id} name={id} {...getInputProps()} />
      <HiddenInput source="threadId" value={useThreadId()} />
      <TextInput
        source="body"
        label={false}
        rows={4}
        fullWidth
        multiline
        sx={{
          border: "1px solid",
          borderColor: "divider",
          ".MuiOutlinedInput-notchedOutline": { borderColor: "transparent !important" },
        }}
      />
      {files.map((file: File) => (
        <div key={file.name} style={{ display: "flex", alignItems: "center", marginTop: "5px" }}>
          <AttachFileIcon style={{ fontSize: 14 }} />
          <Typography
            variant="body2"
            style={{ marginLeft: "6px", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {file.name}
          </Typography>
        </div>
      ))}
    </div>
  );
});

function _PostActions({
  openSelectFilesDialog,
  close,
}: {
  openSelectFilesDialog: false | (() => void);
  close: () => void;
}) {
  const bodyText = useWatch({ name: "body" });
  const primaryColor = useThemeColor("primary");

  return (
    <Row sx={{ mb: 1 }}>
      {openSelectFilesDialog && (
        <Tooltip title="Dokument(e) anhängen" placement="top">
          <IconButton
            size="small"
            color="primary"
            onClick={openSelectFilesDialog}
            sx={{ border: `1px solid ${primaryColor.main}` }}
          >
            <AttachFileIcon />
          </IconButton>
        </Tooltip>
      )}
      <div style={{ flex: 1 }} />
      <Button variant="outlined" size="small" onClick={close} style={{ marginRight: "6px" }}>
        Abbrechen
      </Button>
      <SaveButton icon={<></>} disabled={!bodyText || bodyText.trim().length === 0} />
    </Row>
  );
}
